import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { can } from "@sts/schemas";
import type {
  ChatAttachment,
  ChatMessage,
  ChatThread,
  ChatThreadSummary,
  Colleague,
  ListedDocument,
  Me,
} from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href, useNavigate } from "../app/router.js";
import {
  authorName,
  fileSize,
  otherParticipant,
  threadTitle,
  timeOfDay,
} from "../lib/comms.js";

/**
 * Staff chat.
 *
 * Two things about this subsystem shape every control on the screen, and both
 * are easy to get wrong in a way nobody notices until it has cost somebody
 * something.
 *
 * The history is append-only. DELETE on `chat_messages` is revoked from the
 * application role in the database itself — migration 0012, pinned by
 * `db/privileges.test.ts` — so there is no delete button anywhere below, and
 * there could not be one that worked. What exists instead is redaction, which
 * empties the body, records who did it and why, and leaves the sequence number,
 * the author and the timestamp exactly where they were. A gap in a
 * conversation is rendered as a gap.
 *
 * The read position is a watermark. It is one integer per person per thread and
 * it cannot express "I read message 3 but not message 1", so anything that
 * moves it forward past something the reader has not seen loses that message
 * permanently — the unread count is derived from the position, not from
 * per-message state, and the position never goes backwards. Hence: nothing on
 * this screen marks anything read on its own. Opening a thread does not, and
 * replying does not. Marking read is a button, it says which message it will
 * mark up to, and where earlier messages are off screen it says so before you
 * press it.
 */

/* -------------------------------------------------------------------------- */
/* The thread list                                                            */
/* -------------------------------------------------------------------------- */

interface InboxData {
  readonly threads: readonly ChatThreadSummary[];
  readonly colleagues: readonly Colleague[];
  readonly me: Me;
}

export function Chat(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<InboxData>(
    async () => ({
      threads: await client.chatThreads(),
      colleagues: await client.colleagues(),
      me: await client.me(),
    }),
    [client],
  );

  return (
    <>
      <div className="page-header spread">
        <div>
          <h1>Messages</h1>
          <p className="lede">
            Conversations between people here. Nothing in them can be deleted:
            the history is append-only in the database, so a message can be
            redacted — emptied, with a recorded reason — and never removed.
          </p>
        </div>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
      </div>

      <Async state={state} what="your conversations" reload={reload}>
        {(inbox) => <Inbox inbox={inbox} reload={reload} />}
      </Async>
    </>
  );
}

function Inbox({
  inbox,
  reload,
}: {
  readonly inbox: InboxData;
  readonly reload: () => void;
}): ReactNode {
  const people = useMemo(
    () => new Map(inbox.colleagues.map((c) => [c.userId, c])),
    [inbox.colleagues],
  );

  return (
    <>
      <section>
        <h2>
          Your conversations <span className="count">{inbox.threads.length}</span>
        </h2>
        {inbox.threads.length === 0 ? (
          <p className="empty">
            No conversations yet. You appear here once somebody messages you, or
            once you open one below.
          </p>
        ) : (
          <div className="table-scroll">
            <table>
              <caption className="visually-hidden">
                Your conversations, most recently active first
              </caption>
              <thead>
                <tr>
                  <th scope="col">Conversation</th>
                  <th scope="col">Kind</th>
                  <th scope="col">Unread</th>
                  <th scope="col">Last message</th>
                </tr>
              </thead>
              <tbody>
                {inbox.threads.map((thread) => (
                  <tr key={thread.id}>
                    <th scope="row">
                      <a href={href.chatThread(thread.id)}>
                        {threadTitle(thread, inbox.me.userId, people)}
                      </a>
                    </th>
                    <td>
                      {thread.kind === "job" ? (
                        <>
                          About a job
                          {thread.jobId === null ? null : (
                            <>
                              {" "}
                              <a href={href.job(thread.jobId)}>(open the job)</a>
                            </>
                          )}
                        </>
                      ) : (
                        "Direct"
                      )}
                    </td>
                    <td>
                      {thread.unreadCount === 0 ? (
                        <span className="muted">nothing new</span>
                      ) : (
                        <span className="status-word status-approaching">
                          <span aria-hidden="true">◑ </span>
                          {thread.unreadCount} unread
                        </span>
                      )}
                    </td>
                    <td className="ts">
                      {thread.lastMessageAt === null ? (
                        <span className="muted">nothing said yet</span>
                      ) : (
                        timeOfDay(thread.lastMessageAt)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <StartConversation
        colleagues={inbox.colleagues}
        me={inbox.me}
        onOpened={reload}
      />
    </>
  );
}

/**
 * Opening a direct conversation with a colleague.
 *
 * Idempotent at the API, so pressing this twice cannot produce two half-empty
 * conversations with the same person — which is why it is safe to offer as
 * "open" rather than "create".
 *
 * People whose membership has ended are listed and are not addressable. Listing
 * them is deliberate: their name is what stops their past messages reading as a
 * bare uuid. Making them addressable is refused by the API, so the control that
 * would do it is not offered here either.
 */
function StartConversation({
  colleagues,
  me,
  onOpened,
}: {
  readonly colleagues: readonly Colleague[];
  readonly me: Me;
  readonly onOpened: () => void;
}): ReactNode {
  const { client } = usePortal();
  const navigate = useNavigate();
  const { busy, error, submit } = useSubmit();

  const others = colleagues.filter((c) => c.userId !== me.userId);
  const active = others.filter((c) => c.active);
  const departed = others.filter((c) => !c.active);

  if (!can({ role: me.role, resource: "chat", action: "create" })) {
    return (
      <div className="notice warn" role="note">
        <p>
          Your role may not <strong>create</strong> <strong>chat</strong>, so
          there is no way to open a conversation from here. You can still read
          the ones you are already in. This is a permission, not a fault.
        </p>
      </div>
    );
  }

  const open = (userId: string) =>
    void submit(async () => {
      const thread = await client.openDirectThread(userId);
      onOpened();
      navigate(href.chatThread(thread.id));
    });

  return (
    <section>
      <h2>Start a conversation</h2>
      {error ? <Failure error={error} what="the conversation" /> : null}
      {active.length === 0 ? (
        <p className="empty">
          There is nobody else with an active membership here to talk to.
        </p>
      ) : (
        <ul>
          {active.map((colleague) => (
            <li key={colleague.userId}>
              {colleague.fullName}{" "}
              <span className="muted small">{colleague.role}</span>{" "}
              <button className="link" disabled={busy} onClick={() => open(colleague.userId)}>
                Open a conversation
              </button>
            </li>
          ))}
        </ul>
      )}

      {departed.length === 0 ? null : (
        <>
          <h3 className="small">People who have left</h3>
          <p className="muted small">
            Listed so their past messages carry a name rather than an
            identifier. A conversation cannot be opened with them — the API
            refuses to address somebody whose membership has ended, so there is
            no button here that would fail.
          </p>
          <ul className="small">
            {departed.map((colleague) => (
              <li key={colleague.userId}>{colleague.fullName} (left)</li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* The conversations about one job                                            */
/* -------------------------------------------------------------------------- */

/**
 * Job threads, on the job.
 *
 * Separate from the inbox because they are reached differently and mean
 * something different. The inbox is "conversations I am in"; this is "what has
 * been said about this job", and somebody picking a job up after a colleague
 * left needs the second without having been in any of them.
 *
 * A subject is required — the API refuses a thread without one — and that is
 * the point rather than a validation detail. One unnamed thread per job is the
 * scrolling wall of history this feature exists to replace, so the control asks
 * what the conversation is about before it will open one.
 *
 * Loaded on its own rather than folded into the job's loader, so a role that
 * cannot read chat still gets the rest of the job screen and this section says
 * which grant it was refused instead of failing the page.
 */
export function JobThreads({ jobId }: { readonly jobId: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<{
    readonly threads: readonly ChatThread[];
    readonly me: Me;
  }>(
    async () => ({
      threads: await client.jobChatThreads(jobId),
      me: await client.me(),
    }),
    [client, jobId],
  );

  return (
    <section>
      <h2>Conversations about this job</h2>
      <Async
        state={state}
        what="the conversations about this job"
        reload={reload}
      >
        {({ threads, me }) => (
          <>
            {threads.length === 0 ? (
              <p className="empty">
                Nothing has been discussed about this job here yet.
              </p>
            ) : (
              <ul className="stacked">
                {threads.map((thread) => (
                  <li key={thread.id}>
                    <a href={href.chatThread(thread.id)}>
                      {thread.subject ?? "Untitled thread"}
                    </a>{" "}
                    <span className="muted small ts">
                      {thread.lastMessageAt === null
                        ? "nothing said yet"
                        : `last message ${timeOfDay(thread.lastMessageAt)}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <StartJobThread jobId={jobId} me={me} onOpened={reload} />
          </>
        )}
      </Async>
    </section>
  );
}

function StartJobThread({
  jobId,
  me,
  onOpened,
}: {
  readonly jobId: string;
  readonly me: Me;
  readonly onOpened: () => void;
}): ReactNode {
  const { client } = usePortal();
  const navigate = useNavigate();
  const { busy, error, submit } = useSubmit();
  const [subject, setSubject] = useState("");

  if (!can({ role: me.role, resource: "chat", action: "create" })) {
    return (
      <div className="notice warn" role="note">
        <p>
          Your role may not <strong>create</strong> <strong>chat</strong>, so a
          conversation about this job cannot be started from here. You can still
          read any that already exist. This is a permission, not a fault.
        </p>
      </div>
    );
  }

  return (
    <form
      className="stacked"
      onSubmit={(event) => {
        event.preventDefault();
        void submit(async () => {
          const thread = await client.createJobChatThread(jobId, subject);
          setSubject("");
          onOpened();
          navigate(href.chatThread(thread.id));
        });
      }}
    >
      <div className="field">
        <label htmlFor="job-thread-subject">
          What a new conversation about this job is about
        </label>
        <input
          id="job-thread-subject"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
        />
        <span className="hint">
          Required. A thread carries its subject for the life of the job, and
          one unnamed thread per job is the wall of history this replaces.
        </span>
      </div>
      {error ? <Failure error={error} what="the conversation" /> : null}
      <p className="row-actions">
        <button type="submit" disabled={busy}>
          Start a conversation about this job
        </button>
      </p>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* One conversation                                                           */
/* -------------------------------------------------------------------------- */

interface Loaded {
  readonly thread: ChatThread;
  readonly messages: readonly ChatMessage[];
  readonly colleagues: readonly Colleague[];
  readonly me: Me;
  /** Where this person has read up to, or null if they are not a participant. */
  readonly lastReadSeq: number | null;
  /** The job's vault documents, when this is a job thread and they are readable. */
  readonly documents: readonly ListedDocument[] | null;
}

/** A screenful. The route caps a page at 200; this is what it takes by default. */
const PAGE = 50;

export function Conversation({ id }: { readonly id: string }): ReactNode {
  const { client } = usePortal();
  const [older, setOlder] = useState<readonly ChatMessage[]>([]);

  const { state, reload } = useAsync<Loaded>(async () => {
    const thread = await client.getChatThread(id);
    const [messages, colleagues, me, summaries] = [
      await client.threadMessages(id, { limit: PAGE }),
      await client.colleagues(),
      await client.me(),
      await client.chatThreads(),
    ];
    const mine = summaries.find((t) => t.id === id);
    // A job thread nobody has posted in is readable without being in anyone's
    // list, so "not a participant yet" is a real state rather than an error.
    const lastReadSeq = mine === undefined ? null : mine.lastReadSeq;

    let documents: ListedDocument[] | null = null;
    if (thread.kind === "job" && thread.jobId !== null) {
      // Attaching is the only reason to load these, and a role that cannot read
      // the vault is a permission rather than a fault: the composer says so
      // instead of showing a picker that would refuse.
      documents = await client.jobDocuments(thread.jobId).catch(() => null);
    }
    return { thread, messages, colleagues, me, lastReadSeq, documents };
  }, [client, id]);

  useEffect(() => {
    setOlder([]);
  }, [id]);

  const refresh = useCallback(() => {
    setOlder([]);
    reload();
  }, [reload]);

  return (
    <Async state={state} what="this conversation" reload={refresh}>
      {(loaded) => (
        <Thread
          loaded={loaded}
          older={older}
          onLoadedOlder={(page) => setOlder((held) => [...page, ...held])}
          refresh={refresh}
        />
      )}
    </Async>
  );
}

function Thread({
  loaded,
  older,
  onLoadedOlder,
  refresh,
}: {
  readonly loaded: Loaded;
  readonly older: readonly ChatMessage[];
  readonly onLoadedOlder: (page: readonly ChatMessage[]) => void;
  readonly refresh: () => void;
}): ReactNode {
  const { thread, me } = loaded;
  const people = useMemo(
    () => new Map(loaded.colleagues.map((c) => [c.userId, c])),
    [loaded.colleagues],
  );
  const shown = useMemo(
    () => [...older, ...loaded.messages],
    [older, loaded.messages],
  );
  const other = otherParticipant(thread, me.userId);

  return (
    <>
      <div className="page-header spread">
        <div>
          <h1>{threadTitle(thread, me.userId, people)}</h1>
          <p className="lede">
            {thread.kind === "job" ? (
              <>
                About a job.{" "}
                {thread.jobId === null ? null : (
                  <a href={href.job(thread.jobId)}>Open the job</a>
                )}
              </>
            ) : (
              <>
                A direct conversation
                {other === null ? "" : ` with ${authorName(other, me.userId, people)}`}.
              </>
            )}
          </p>
        </div>
        <a href={href.chat()}>All conversations</a>
      </div>

      <AddColleague loaded={loaded} onAdded={refresh} />

      <Earlier loaded={loaded} shown={shown} onLoadedOlder={onLoadedOlder} />

      <section>
        <h2 className="visually-hidden">Messages</h2>
        {shown.length === 0 ? (
          <p className="empty">Nothing has been said in here yet.</p>
        ) : (
          <ol className="stack">
            {shown.map((message) => (
              <Message
                key={message.id}
                message={message}
                me={me}
                people={people}
                onRedacted={refresh}
              />
            ))}
          </ol>
        )}
      </section>

      <ReadPosition loaded={loaded} shown={shown} onMarked={refresh} />
      <Composer loaded={loaded} onPosted={refresh} />

      <p className="muted small">
        Nothing in this conversation can be deleted. The database refuses it —
        DELETE on chat messages is revoked from the application entirely — so
        what a moderator can do is redact a message, which empties it and
        records who did it and why, leaving its place in the sequence intact.
      </p>
    </>
  );
}

/**
 * Pulls a colleague into a job conversation so it shows up in their own list.
 *
 * Direct threads have exactly two people and the API refuses a third, so this
 * offers nothing on one. And there is no route that lists who is already a
 * participant — adding one is the whole surface — so the copy says that
 * plainly rather than implying a list that has been checked. Adding somebody
 * already in changes nothing: the database upserts on conflict, so this is
 * safe to offer blind.
 */
function AddColleague({
  loaded,
  onAdded,
}: {
  readonly loaded: Loaded;
  readonly onAdded: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [added, setAdded] = useState<string | null>(null);
  const { thread, me, colleagues } = loaded;

  if (thread.kind !== "job") return null;
  if (!can({ role: me.role, resource: "chat", action: "create" })) return null;

  // The API refuses to add somebody whose membership has ended, the same
  // refusal `openDirectThread` gives — so only an active colleague is offered.
  const others = colleagues.filter((c) => c.userId !== me.userId && c.active);
  if (others.length === 0) return null;

  const add = (colleague: Colleague) =>
    void submit(async () => {
      await client.addChatParticipant(thread.id, colleague.userId);
      setAdded(colleague.fullName);
      onAdded();
    });

  return (
    <details>
      <summary>Add a colleague to this conversation</summary>
      <p className="muted small">
        Pulls them into this thread so it appears in their own list of
        conversations. It grants them nothing they could not already read — a
        job thread is readable by anyone who can read the job — it only
        decides whose sidebar it shows up in.
      </p>
      <ul>
        {others.map((colleague) => (
          <li key={colleague.userId}>
            {colleague.fullName}{" "}
            <span className="muted small">{colleague.role}</span>{" "}
            <button className="link" disabled={busy} onClick={() => add(colleague)}>
              Add
            </button>
          </li>
        ))}
      </ul>
      {added !== null ? (
        <p className="notice" role="status">
          {added} has been added — or already was; there is no route that
          would tell the two apart.
        </p>
      ) : null}
      {error ? <Failure error={error} what="adding that colleague" /> : null}
    </details>
  );
}

/**
 * The messages above this page, and a way to actually read them.
 *
 * This exists because of the watermark. Sequence numbers are allocated per
 * thread as max+1 and nothing can delete a row, so a first message numbered
 * higher than 1 means there are earlier messages that are not on this screen —
 * and marking read from here would clear them too. Offering the button without
 * offering a way to read them first would be offering a trap.
 */
function Earlier({
  loaded,
  shown,
  onLoadedOlder,
}: {
  readonly loaded: Loaded;
  readonly shown: readonly ChatMessage[];
  readonly onLoadedOlder: (page: readonly ChatMessage[]) => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const first = shown[0];
  if (first === undefined || first.seq <= 1) return null;

  const missing = first.seq - 1;

  return (
    <div className="notice" role="note">
      <p>
        {missing} earlier {missing === 1 ? "message is" : "messages are"} not on
        this screen.
      </p>
      {error ? <Failure error={error} what="the earlier messages" /> : null}
      <button
        className="secondary"
        disabled={busy}
        onClick={() =>
          void submit(async () => {
            onLoadedOlder(
              await client.threadMessages(loaded.thread.id, {
                limit: PAGE,
                beforeSeq: first.seq,
              }),
            );
          })
        }
      >
        Load the earlier messages
      </button>
    </div>
  );
}

function Message({
  message,
  me,
  people,
  onRedacted,
}: {
  readonly message: ChatMessage;
  readonly me: Me;
  readonly people: ReadonlyMap<string, Colleague>;
  readonly onRedacted: () => void;
}): ReactNode {
  return (
    <li className="row" id={`message-${message.seq}`}>
      <article>
        <p className="small">
          <strong>{authorName(message.authorUserId, me.userId, people)}</strong>{" "}
          <span className="muted ts">{timeOfDay(message.createdAt)}</span>{" "}
          <span className="muted mono">#{message.seq}</span>
        </p>
        {message.redacted ? (
          <Redacted message={message} me={me} people={people} />
        ) : (
          <>
            <p>{message.body}</p>
            <Attachments attachments={message.attachments} />
            <Redact message={message} me={me} onRedacted={onRedacted} />
          </>
        )}
      </article>
    </li>
  );
}

/**
 * A redacted message, rendered as the gap it is.
 *
 * Not hidden and not skipped. The row is still there, still numbered, still
 * attributed and still timestamped, because the history is append-only and a
 * screen that quietly closed the gap would be claiming a conversation that
 * never happened.
 */
function Redacted({
  message,
  me,
  people,
}: {
  readonly message: ChatMessage;
  readonly me: Me;
  readonly people: ReadonlyMap<string, Colleague>;
}): ReactNode {
  return (
    <div className="notice warn">
      <p>
        <span className="status-word status-approaching">
          <span aria-hidden="true">◔ </span>
          Redacted
        </span>{" "}
        This message was emptied by{" "}
        {authorName(message.redactedBy, me.userId, people)}
        {message.redactedAt === null ? "" : ` on ${timeOfDay(message.redactedAt)}`}.
      </p>
      <p className="small">
        Reason:{" "}
        {message.redactionReason ?? (
          <span className="muted">none recorded</span>
        )}
      </p>
    </div>
  );
}

/**
 * Redaction, offered only to the roles that hold it.
 *
 * `chat: delete` is an administrative grant — owner and admin today — because
 * emptying somebody else's message is a moderation act rather than a personal
 * one. Everybody else gets the sentence rather than a button that would come
 * back 403 every time.
 */
function Redact({
  message,
  me,
  onRedacted,
}: {
  readonly message: ChatMessage;
  readonly me: Me;
  readonly onRedacted: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [reason, setReason] = useState("");
  const [asking, setAsking] = useState(false);

  if (!can({ role: me.role, resource: "chat", action: "delete" })) return null;

  if (!asking) {
    return (
      <p className="row-actions">
        <button className="link" onClick={() => setAsking(true)}>
          Redact this message
        </button>
      </p>
    );
  }

  return (
    <form
      className="stacked"
      onSubmit={(event) => {
        event.preventDefault();
        void submit(async () => {
          await client.redactChatMessage(message.id, reason);
          setAsking(false);
          setReason("");
          onRedacted();
        });
      }}
    >
      <div className="field">
        <label htmlFor={`reason-${message.id}`}>
          Why this message is being redacted
        </label>
        <input
          id={`reason-${message.id}`}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <span className="hint">
          Recorded permanently against the message. The text is emptied and the
          message stays where it is — there is no way to remove it and no way to
          undo this.
        </span>
      </div>
      {error ? <Failure error={error} what="the redaction" /> : null}
      <p className="row-actions">
        <button type="submit" disabled={busy}>
          Redact it
        </button>
        <button type="button" className="secondary" onClick={() => setAsking(false)}>
          Leave it alone
        </button>
      </p>
    </form>
  );
}

/**
 * What a message carries with it.
 *
 * A `document` attachment is a pointer into the evidence vault and copies
 * nothing; an `upload` is an ad-hoc file. Neither publishes a storage key, so
 * there is nothing here to link to — the filename, the size and the content
 * hash are what a reader needs, and none of them is a path.
 */
function Attachments({
  attachments,
}: {
  readonly attachments: readonly ChatAttachment[];
}): ReactNode {
  if (attachments.length === 0) return null;
  return (
    <ul className="small">
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          {attachment.kind === "document" ? (
            <>
              A document from the evidence vault{" "}
              <span className="mono">{attachment.documentId}</span>
            </>
          ) : (
            <>
              {attachment.filename ?? "an unnamed file"}
              {attachment.byteSize === null ? null : (
                <> · {fileSize(attachment.byteSize)}</>
              )}
              {attachment.contentHash === null ? null : (
                <>
                  {" "}
                  · <span className="mono">{attachment.contentHash}</span>
                </>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * The read position, moved deliberately and never by a side effect.
 *
 * The button says which message it marks up to, because the position is a
 * watermark and pressing it clears everything below that number as well. That
 * is not a detail: a reader who has scrolled through three of eleven messages
 * and pressed a button labelled "mark read" has lost the other eight, with no
 * way to get them back.
 */
function ReadPosition({
  loaded,
  shown,
  onMarked,
}: {
  readonly loaded: Loaded;
  readonly shown: readonly ChatMessage[];
  readonly onMarked: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();

  const highest = shown.reduce((max, m) => (m.seq > max ? m.seq : max), 0);
  const readTo = loaded.lastReadSeq;

  if (highest === 0) return null;

  const unseen =
    readTo === null
      ? shown.filter((m) => m.authorUserId !== loaded.me.userId).length
      : shown.filter(
          (m) => m.seq > readTo && m.authorUserId !== loaded.me.userId,
        ).length;

  return (
    <section>
      <h2 className="small">Where you have read up to</h2>
      <p>
        {readTo === null ? (
          <>
            You are not down as a participant in this conversation yet, so
            nothing is marked read. Posting, or marking read below, enrols you.
          </>
        ) : readTo === 0 ? (
          <>Nothing in this conversation is marked read for you.</>
        ) : (
          <>
            Marked read up to message <span className="mono">#{readTo}</span>.
          </>
        )}{" "}
        {unseen === 0
          ? "There is nothing new on this screen."
          : `${unseen} ${unseen === 1 ? "message" : "messages"} on this screen ${
              unseen === 1 ? "is" : "are"
            } newer than that.`}
      </p>
      {error ? <Failure error={error} what="the read position" /> : null}
      <p className="row-actions">
        <button
          disabled={busy || (readTo !== null && readTo >= highest)}
          onClick={() =>
            void submit(async () => {
              await client.markThreadRead(loaded.thread.id, highest);
              onMarked();
            })
          }
        >
          Mark read up to message #{highest}
        </button>
      </p>
      <p className="muted small">
        The read position is a single number per conversation, so it cannot say
        &ldquo;read message 3 but not message 1&rdquo;. Marking read to #{highest}{" "}
        marks everything below it read as well, and it cannot be moved back.
        Nothing here does it for you: opening this conversation did not, and
        replying will not.
      </p>
    </section>
  );
}

/** Writing a message. Posting deliberately does not move the read position. */
function Composer({
  loaded,
  onPosted,
}: {
  readonly loaded: Loaded;
  readonly onPosted: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [body, setBody] = useState("");
  const [documentId, setDocumentId] = useState("");

  if (!can({ role: loaded.me.role, resource: "chat", action: "create" })) {
    return (
      <div className="notice warn" role="note">
        <p>
          Your role may not <strong>create</strong> <strong>chat</strong>, so
          there is no box to write in. You can read this conversation. This is a
          permission, not a fault.
        </p>
      </div>
    );
  }

  return (
    <section>
      <h2>Say something</h2>
      <form
        className="stacked"
        onSubmit={(event) => {
          event.preventDefault();
          void submit(async () => {
            await client.postChatMessage(loaded.thread.id, {
              body,
              ...(documentId === ""
                ? {}
                : { attachments: [{ documentId }] }),
            });
            setBody("");
            setDocumentId("");
            onPosted();
          });
        }}
      >
        <div className="field">
          <label htmlFor="chat-body">Message</label>
          <textarea
            id="chat-body"
            rows={3}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        </div>

        {loaded.thread.kind === "job" ? (
          loaded.documents === null ? (
            <p className="muted small">
              A document from the vault cannot be attached here: this job&apos;s
              documents could not be read with your role. The message itself
              still sends.
            </p>
          ) : loaded.documents.length === 0 ? (
            <p className="muted small">
              There are no documents on this job to attach yet.
            </p>
          ) : (
            <div className="field">
              <label htmlFor="chat-attachment">
                Attach a document from the vault
              </label>
              <select
                id="chat-attachment"
                value={documentId}
                onChange={(event) => setDocumentId(event.target.value)}
              >
                <option value="">Nothing attached</option>
                {loaded.documents.map((document) => (
                  <option key={document.id} value={document.id}>
                    {document.title}
                  </option>
                ))}
              </select>
              <span className="hint">
                A reference, not a copy. The file stays in the vault and this
                message points at it; there is no route that takes bytes from a
                browser.
              </span>
            </div>
          )
        ) : null}

        {error ? <Failure error={error} what="your message" /> : null}
        <p className="row-actions">
          <button type="submit" disabled={busy}>
            Send
          </button>
        </p>
      </form>
      <p className="muted small">
        Sending does not mark anything read. Replying from a notification
        without scrolling up would otherwise swallow whatever is above your
        message, permanently.
      </p>
    </section>
  );
}
